export type ToneKind = 'ring' | 'ringback';
export interface Ringtone {
  start(kind: ToneKind): void;
  stop(): void;
  /** Bei einer Nutzergeste aufrufen, damit der Browser späteres Klingeln erlaubt (Autoplay-Policy). */
  unlock(): void;
}

interface OscillatorLike {
  frequency: { value: number };
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
}
interface GainLike {
  gain: { value: number };
  connect(node: unknown): void;
}
export interface AudioContextLike {
  currentTime: number;
  destination: unknown;
  createOscillator(): OscillatorLike;
  createGain(): GainLike;
  resume(): Promise<void>;
}

const PATTERNS: Record<ToneKind, { freqs: number[]; onSec: number; periodMs: number }> = {
  ring: { freqs: [440, 480], onSec: 1, periodMs: 3000 },
  ringback: { freqs: [425], onSec: 1, periodMs: 5000 },
};

export function createRingtone(
  createContext: () => AudioContextLike = () => new AudioContext() as unknown as AudioContextLike,
): Ringtone {
  let ctx: AudioContextLike | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let active: ToneKind | null = null;

  const beep = (kind: ToneKind) => {
    if (!ctx) return;
    const { freqs, onSec } = PATTERNS[kind];
    const gain = ctx.createGain();
    gain.gain.value = 0.15;
    gain.connect(ctx.destination);
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.frequency.value = f;
      osc.connect(gain);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + onSec);
    }
  };

  const ensureContext = (): boolean => {
    try {
      ctx ??= createContext();
      void ctx.resume().catch(() => undefined);
      return true;
    } catch {
      ctx = null;
      return false;
    }
  };

  const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
    active = null;
  };

  return {
    start(kind) {
      if (active === kind) return;
      stop();
      if (!ensureContext()) return;
      active = kind;
      beep(kind);
      timer = setInterval(() => beep(kind), PATTERNS[kind].periodMs);
    },
    stop,
    unlock() {
      ensureContext();
    },
  };
}
